import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { getStockInstruments, getStockNews } from "@/api/stock-news";
import type { StockNewsPage } from "@crypto-trading/common/browser";

const deduplicatePages = (data: {
  pages: StockNewsPage[];
  pageParams: unknown[];
}): { pages: StockNewsPage[]; pageParams: unknown[] } => {
  const seen = new Set<string>();
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      }),
    })),
  };
};

export const useInfiniteStockNews = (
  userId: string,
  limit: number,
  enabled: boolean = true
) => {
  return useInfiniteQuery({
    queryKey: ["stock-news", "infinite", userId, limit],
    queryFn: ({ pageParam = 1 }): Promise<StockNewsPage> =>
      getStockNews(userId, { limit, page: pageParam }),
    enabled: enabled && !!userId,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    select: deduplicatePages,
    initialPageParam: 1,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
};

export const useStockInstruments = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["stock-instruments"],
    queryFn: getStockInstruments,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};
